import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  BarChart3,
  ClipboardCheck,
  FileText,
  Sparkles,
} from 'lucide-react';
import { accountDisplayName, DashboardHeader, DashboardSidebar } from './Dashboard';
import './AutoReport.css';
import { API_BASE_URL } from "./utils/apiBase";


// Character limit configuration
const CHAR_LIMIT_PRESETS = [300, 400, 500, 750, 900];
const DEFAULT_CHAR_LIMIT = 500;
const MIN_CHAR_LIMIT = 200;
const MAX_CHAR_LIMIT = 900;

const AutoReport = ({ user }) => {
  const navigate = useNavigate();

  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Student Input
  const [studentNamesInput, setStudentNamesInput] = useState('');
  const [studentNames, setStudentNames] = useState([]);
  const [nameError, setNameError] = useState('');

  // Step 2: Shared Class Details
  const [classInfo, setClassInfo] = useState({
    className: '',
    subject: '',
    term: '',
    unitsCovered: ''
  });

  // Character limit (Part 1 & 2)
  const [characterLimit, setCharacterLimit] = useState(DEFAULT_CHAR_LIMIT);
  const [charLimitError, setCharLimitError] = useState('');

  // Step 3: Individual Student Details
  const [studentDetails, setStudentDetails] = useState([]);

  // Step 4: Generated Reports
  const [reports, setReports] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // Copy state tracking per report
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pageNotice, setPageNotice] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('darkMode') === 'true'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [shellSearch, setShellSearch] = useState('');

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    document.documentElement.setAttribute(
      'data-dark-mode',
      darkMode ? 'true' : 'false'
    );
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'darkMode') setDarkMode(event.newValue === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Populate student details when names are parsed
  useEffect(() => {
    if (studentNames.length > 0) {
      const initialDetails = studentNames.map(name => ({
        name,
        level: 5,
        positives: '',
        areasToImprove: ''
      }));
      setStudentDetails(initialDetails);
    }
  }, [studentNames]);

  // ── Step 1 handlers ─────────────────────────────────────────────────────────

  const handleStudentNamesChange = (e) => {
    setStudentNamesInput(e.target.value);
  };

  const processStudentNames = () => {
    const names = studentNamesInput
      .split(',')
      .map(name => name.trim())
      .filter(name => name !== '');

    if (names.length === 0) {
      setNameError('Please enter at least one student name.');
      return;
    }
    if (names.length > 30) {
      setNameError('You can only enter up to 30 students at a time.');
      return;
    }

    setNameError('');
    setStudentNames(names);
    setCurrentStep(2);
  };

  const updateStudentName = (index, newName) => {
    const updatedNames = [...studentNames];
    updatedNames[index] = newName;
    setStudentNames(updatedNames);

    const updatedDetails = [...studentDetails];
    updatedDetails[index].name = newName;
    setStudentDetails(updatedDetails);
  };

  // ── Step 2 handlers ─────────────────────────────────────────────────────────

  const handleClassInfoChange = (e) => {
    const { name, value } = e.target;
    setClassInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleCharLimitSelect = (limit) => {
    setCharacterLimit(limit);
    setCharLimitError('');
  };

  // Frontend validation for character limit (Part 2)
  const validateCharLimit = (val) => {
    const num = Number(val);
    if (isNaN(num) || !Number.isInteger(num)) {
      setCharLimitError('Please select a valid character limit.');
      return false;
    }
    if (num < MIN_CHAR_LIMIT) {
      setCharLimitError(`Minimum allowed is ${MIN_CHAR_LIMIT} characters.`);
      return false;
    }
    if (num > MAX_CHAR_LIMIT) {
      setCharLimitError(`Maximum allowed is ${MAX_CHAR_LIMIT} characters.`);
      return false;
    }
    setCharLimitError('');
    return true;
  };

  const proceedToStudentDetails = () => {
    if (!validateCharLimit(characterLimit)) return;
    setCurrentStep(3);
  };

  // ── Step 3 handlers ─────────────────────────────────────────────────────────

  const handleStudentDetailChange = (index, field, value) => {
    const updatedDetails = [...studentDetails];
    updatedDetails[index][field] = value;
    setStudentDetails(updatedDetails);
  };

  // ── Report generation ────────────────────────────────────────────────────────

  const generateReports = async () => {
    if (!user || !user.uid) {
      setPageNotice({ type: 'error', text: 'You must be logged in to generate reports.' });
      return;
    }

    // Clamp the character limit on the frontend before sending (Part 2)
    const safeLimit = Math.min(
      Math.max(Number(characterLimit) || DEFAULT_CHAR_LIMIT, MIN_CHAR_LIMIT),
      MAX_CHAR_LIMIT
    );

    setPageNotice(null);
    setIsGenerating(true);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("You must be logged in to generate reports.");
      }

      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/api/generate-reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
          students: studentDetails,
          classInfo,
          teacherId: user.uid,
          characterLimit: safeLimit  // Part 2: include limit in request body
        }),
      });

      if (!response.ok) {
        // Handle credit-related errors
        if (response.status === 402) {
          const errorData = await response.json();
          setPageNotice({
            type: 'error',
            text: errorData.error ||
              "You have insufficient credits to generate these reports. Please top up your credits and try again.",
          });
          setIsGenerating(false);
          return;
        }
        // Handle authentication errors
        if (response.status === 401) {
          const errorData = await response.json();
          setPageNotice({
            type: 'error',
            text: errorData.error || "Authentication failed. Please log in again.",
          });
          setIsGenerating(false);
          return;
        }
        // Handle authorisation errors
        if (response.status === 403) {
          const errorData = await response.json();
          setPageNotice({
            type: 'error',
            text: errorData.error || "You are not authorised to generate reports.",
          });
          setIsGenerating(false);
          return;
        }
        throw new Error(`API returned status ${response.status}`);
      }

      const data = await response.json();

      if (data?.reports) {
        // Attach level information to each report (preserving existing structure)
        const reportsWithLevel = data.reports.map(report => {
          const studentDetail = studentDetails.find(s => s.name === report.name);
          return {
            ...report,
            level: studentDetail ? studentDetail.level : null
          };
        });
        setReports(reportsWithLevel);
        setCurrentStep(4);

        if (data.usage && data.usage.total_tokens) {
          console.log(`Total tokens used for reports: ${data.usage.total_tokens}`);
        }
      } else {
        setPageNotice({ type: 'error', text: 'Reports could not be generated. Please try again.' });
      }
    } catch (error) {
      console.error("Error calling /api/generate-reports:", error);
      setPageNotice({
        type: 'error',
        text: 'Something went wrong while generating reports. Please check your connection and try again.',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Report actions (existing, preserved) ────────────────────────────────────

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const downloadAsCSV = () => {
    let csvContent = "Student Name,Level,Report\n";
    reports.forEach(report => {
      const escapedReport = report.report.replace(/"/g, '""');
      csvContent += `"${report.name}","${report.level}","${escapedReport}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${classInfo.className}_${classInfo.subject}_Reports.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printReports = () => {
    const printWindow = window.open('', '_blank');

    let htmlContent = `
      <html>
        <head>
          <title>${classInfo.className} ${classInfo.subject} Reports</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .report { margin-bottom: 30px; page-break-after: always; }
            h2 { color: #2c3e50; }
            p { line-height: 1.6; }
            .header { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${classInfo.className} ${classInfo.subject} Reports — ${classInfo.term}</h1>
          </div>
    `;

    reports.forEach(report => {
      htmlContent += `
        <div class="report">
          <h2>${report.name} — Level ${report.level}</h2>
          <p>${report.report.replace(/\n/g, '<br/>')}</p>
        </div>
      `;
    });

    htmlContent += `</body></html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = function () {
      printWindow.print();
    };
  };

  const startNewBatch = () => {
    setCurrentStep(1);
    setStudentNamesInput('');
    setStudentNames([]);
    setNameError('');
    setClassInfo({ className: '', subject: '', term: '', unitsCovered: '' });
    setCharacterLimit(DEFAULT_CHAR_LIMIT);
    setStudentDetails([]);
    setReports([]);
    setPageNotice(null);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * Extract the main report paragraph only (everything before the first target
   * bullet line). The backend separates the paragraph from the two targets with
   * a blank line; targets begin with "- " or "• ".
   */
  const extractParagraphOnly = (reportText) => {
    if (!reportText) return '';
    const lines = reportText.split('\n');
    const paragraphLines = [];
    for (const line of lines) {
      const trimmed = line.trim();
      // Stop at the first target bullet point
      if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
        break;
      }
      paragraphLines.push(line);
    }
    return paragraphLines.join('\n').trim();
  };

  const getLevelLabel = (level) => {
    if (level <= 2) return 'Emerging';
    if (level <= 4) return 'Developing';
    if (level === 5) return 'Secure';
    if (level <= 7) return 'Proficient';
    return 'Accomplished';
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    try {
      await getAuth().signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out', error);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (error) {
      console.error('Could not copy teacher code', error);
    }
  };

  const role = user?.role === 'admin' ? 'admin' : 'teacher';
  const username = accountDisplayName(user, 'StudyDen teacher');

  // ── Render: Progress Indicator ───────────────────────────────────────────────

  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        {[1, 2, 3, 4].map(step => (
          <div
            key={step}
            className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
          >
            <div className="step-circle">
              {currentStep > step ? (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : step}
            </div>
            <div className="step-label">
              {step === 1 ? 'Students' :
               step === 2 ? 'Class Details' :
               step === 3 ? 'Student Info' :
               'Reports'}
            </div>
          </div>
        ))}
        <div className="progress-bar-track">
          <div
            className="progress-bar-fill"
            style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  // ── Render: Step 1 ───────────────────────────────────────────────────────────

  const renderStudentInput = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <div className="step-icon step-icon-1">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h2>Student Names</h2>
            <p className="step-description">
              Enter the names of the students you want to generate reports for, separated by commas.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="studentNames">
            Student names
            <span className="label-hint"> — separated by commas, up to 30</span>
          </label>
          <textarea
            id="studentNames"
            className="names-textarea"
            placeholder="e.g. Sarah, James, Aisha, Mohammed, Priya"
            value={studentNamesInput}
            onChange={handleStudentNamesChange}
          />
          {nameError && (
            <div className="error-message">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {nameError}
            </div>
          )}
          <div className="field-hint">Separate each name with a comma. You can review and edit names on this page before continuing.</div>
        </div>

        <div className="button-group button-group-right">
          <button className="btn btn-primary" onClick={processStudentNames}>
            Continue
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {studentNames.length > 0 && (
          <div className="student-name-edit">
            <h3>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Review &amp; Edit Names ({studentNames.length})
            </h3>
            <div className="name-edit-grid">
              {studentNames.map((name, index) => (
                <div key={index} className="form-group name-edit-item">
                  <label htmlFor={`student-${index}`}>Student {index + 1}</label>
                  <input
                    id={`student-${index}`}
                    type="text"
                    value={name}
                    onChange={(e) => updateStudentName(index, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Render: Step 2 ───────────────────────────────────────────────────────────

  const renderClassDetails = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <div className="step-icon step-icon-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h2>Class Details &amp; Report Settings</h2>
            <p className="step-description">
              Provide the class information and choose your report character limit.
            </p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="className">Class</label>
            <input
              type="text"
              id="className"
              name="className"
              placeholder="e.g. 9A"
              value={classInfo.className}
              onChange={handleClassInfoChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              placeholder="e.g. Computing"
              value={classInfo.subject}
              onChange={handleClassInfoChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="term">Term</label>
            <input
              type="text"
              id="term"
              name="term"
              placeholder="e.g. Term 3"
              value={classInfo.term}
              onChange={handleClassInfoChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="unitsCovered">Units Covered</label>
          <textarea
            id="unitsCovered"
            name="unitsCovered"
            placeholder="e.g. Unit 1: Data Representation, Unit 2: Python Programming"
            value={classInfo.unitsCovered}
            onChange={handleClassInfoChange}
          />
        </div>

        {/* ── Report Settings Card (Part 1) ─────────────────────── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 4v8m0 3v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>Report Settings</h3>
          </div>

          <div className="settings-card-body">
            <label className="settings-label">
              Character limit per report paragraph
              <span className="settings-hint">
                Choose the maximum length for the main report paragraph. Targets are separate and not included in this limit.
              </span>
            </label>

            <div className="char-limit-presets">
              {CHAR_LIMIT_PRESETS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  className={`preset-btn ${characterLimit === preset ? 'preset-btn-active' : ''}`}
                  aria-pressed={characterLimit === preset}
                  onClick={() => handleCharLimitSelect(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>

            {charLimitError && (
              <div className="error-message">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {charLimitError}
              </div>
            )}

            <div className="char-limit-summary">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9c0-1.121-.175-2.2-.518-3.203z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Reports will be generated with a maximum of{' '}
              <strong>{characterLimit} characters</strong> per report paragraph.
            </div>
          </div>
        </div>

        <div className="button-group">
          <button className="btn btn-secondary" onClick={() => setCurrentStep(1)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <button className="btn btn-primary" onClick={proceedToStudentDetails}>
            Continue
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // ── Render: Step 3 ───────────────────────────────────────────────────────────

  const renderStudentDetails = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <div className="step-icon step-icon-3">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h2>Individual Student Details</h2>
            <p className="step-description">
              Fill in the details for each student. These are used to personalise every report.
            </p>
          </div>
        </div>

        {/* Generation summary bar */}
        <div className="generation-info-bar">
          <div className="generation-info-item">
            <span className="info-label">Students</span>
            <span className="info-value">{studentDetails.length}</span>
          </div>
          <div className="generation-info-divider" />
          <div className="generation-info-item">
            <span className="info-label">Subject</span>
            <span className="info-value">{classInfo.subject || '—'}</span>
          </div>
          <div className="generation-info-divider" />
          <div className="generation-info-item">
            <span className="info-label">Class</span>
            <span className="info-value">{classInfo.className || '—'}</span>
          </div>
          <div className="generation-info-divider" />
          <div className="generation-info-item">
            <span className="info-label">Char limit</span>
            <span className="info-value">{characterLimit}</span>
          </div>
        </div>

        <div className="student-details-container">
          {studentDetails.map((student, index) => (
            <div key={index} className="student-card">
              <div className="student-card-header">
                <div className="student-avatar">
                  {student.name.charAt(0).toUpperCase()}
                </div>
                <h3>{student.name}</h3>
              </div>

              <div className="form-group">
                <label htmlFor={`level-${index}`}>Level (1–9)</label>
                <select
                  id={`level-${index}`}
                  value={student.level}
                  onChange={(e) => handleStudentDetailChange(index, 'level', parseInt(e.target.value))}
                >
                  <option value={1}>1 – Emerging</option>
                  <option value={2}>2 – Emerging</option>
                  <option value={3}>3 – Developing</option>
                  <option value={4}>4 – Developing</option>
                  <option value={5}>5 – Secure</option>
                  <option value={6}>6 – Proficient</option>
                  <option value={7}>7 – Proficient</option>
                  <option value={8}>8 – Accomplished</option>
                  <option value={9}>9 – Accomplished</option>
                </select>
                <div className="level-badge">
                  {getLevelLabel(student.level)}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor={`positives-${index}`}>Positives to mention</label>
                <textarea
                  id={`positives-${index}`}
                  placeholder="e.g. Strong Python skills, completed all homework on time…"
                  value={student.positives}
                  onChange={(e) => handleStudentDetailChange(index, 'positives', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor={`improve-${index}`}>Areas to improve</label>
                <textarea
                  id={`improve-${index}`}
                  placeholder="e.g. Needs to work more independently, struggles with exam technique…"
                  value={student.areasToImprove}
                  onChange={(e) => handleStudentDetailChange(index, 'areasToImprove', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="button-group">
          <button className="btn btn-secondary" onClick={() => setCurrentStep(2)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <button
            className={`btn btn-generate${isGenerating ? ' btn-loading' : ''}`}
            onClick={generateReports}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Generating reports…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Generate {studentDetails.length} Report{studentDetails.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>

        {isGenerating && (
          <div className="generating-notice" role="status" aria-live="polite">
            <div className="generating-notice-inner">
              <span className="spinner spinner-sm" aria-hidden="true" />
              <span>
                Generating personalised reports for {studentDetails.length} student{studentDetails.length !== 1 ? 's' : ''}.
                This may take a moment — please do not close this tab.
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Render: Step 4 ───────────────────────────────────────────────────────────

  const renderReports = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <div className="step-icon step-icon-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9c0-1.121-.175-2.2-.518-3.203z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2>Generated Reports</h2>
            <p className="step-description">
              {reports.length > 0
                ? `${reports.length} report${reports.length !== 1 ? 's' : ''} generated for ${classInfo.subject}${classInfo.className ? ` — ${classInfo.className}` : ''}${classInfo.term ? `, ${classInfo.term}` : ''}.`
                : 'Your generated reports will appear here.'}
            </p>
          </div>
        </div>

        <div className="reports-container">
          {reports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <p>No reports have been generated yet. Go back and complete the previous steps.</p>
            </div>
          ) : (
            reports.map((report, index) => {
              // Measure the main paragraph only — targets are separate and not
              // counted against the character limit (matches backend behaviour).
              const paragraphText = extractParagraphOnly(report.report);
              const paragraphCharCount = paragraphText.length;
              const isOverLimit = paragraphCharCount > characterLimit;

              return (
                <div
                  key={index}
                  className={`report-card${isOverLimit ? ' report-card-warning' : ''}`}
                >
                  <div className="report-card-header">
                    <div className="report-student-info">
                      <div className="report-avatar">
                        {report.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="report-student-name">{report.name}</h3>
                        <span className="report-level-badge">
                          Level {report.level} — {getLevelLabel(report.level)}
                        </span>
                      </div>
                    </div>

                    <button
                      className={`btn-copy${copiedIndex === index ? ' btn-copy-success' : ''}`}
                      onClick={() => copyToClipboard(report.report, index)}
                      title="Copy report to clipboard"
                    >
                      {copiedIndex === index ? (
                        <>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          Copy
                        </>
                      )}
                    </button>
                  </div>

                  <p className="report-text">{report.report}</p>

                  {/* Part 7: Character count — paragraph only */}
                  <div className="report-card-footer">
                    <div className="report-char-info">
                      <div className={`char-count${isOverLimit ? ' char-count-warning' : ''}`}>
                        {isOverLimit && (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2"/>
                            <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        )}
                        Report paragraph: {paragraphCharCount} / {characterLimit} characters
                        {isOverLimit && ' — exceeds limit'}
                      </div>
                      <div className="targets-note">
                        Targets are separate from the paragraph limit.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Export options (preserved) */}
        {reports.length > 0 && (
          <div className="download-options">
            <button className="btn btn-export csv-button" onClick={downloadAsCSV}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Download as CSV
            </button>
            <button className="btn btn-export pdf-button" onClick={printReports}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <polyline points="6 9 6 2 18 2 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2"/>
                <rect x="6" y="14" width="12" height="8" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Print as PDF
            </button>
          </div>
        )}

        <div className="button-group button-group-center">
          <button className="btn btn-secondary" onClick={startNewBatch}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Start New Batch
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <div className={`sd-dashboard sd-brand-theme sd-role-${role} ar-page${darkMode ? ' sd-dark' : ''}`}>
      <DashboardSidebar
        role={role}
        username={username}
        officialDashboardBrand
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ''}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/auto-report"
      />
      <div className="sd-workspace ar-workspace">
        <DashboardHeader
          username={username}
          role={role}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={shellSearch}
          onSearch={setShellSearch}
        />
        <main className="ar-main">
          <section className="ar-hero" aria-labelledby="ar-page-title">
            <span className="ar-hero-arc ar-hero-arc-top" aria-hidden="true" />
            <span className="ar-hero-arc ar-hero-arc-bottom" aria-hidden="true" />
            <div className="ar-hero-copy">
              <button type="button" className="ar-hero-back" onClick={goToDashboard}>
                <span aria-hidden="true">←</span> Dashboard
              </button>
              <p className="ar-eyebrow">Reporting</p>
              <h1 id="ar-page-title">AutoReport</h1>
              <p>Turn teacher insights and student performance details into clear, useful reports.</p>
            </div>
            <div className="ar-hero-art" aria-hidden="true">
              <span className="ar-hero-icon"><FileText size={45} /></span>
              <i className="ar-orbit ar-orbit-one"><BarChart3 size={19} /></i>
              <i className="ar-orbit ar-orbit-two"><ClipboardCheck size={19} /></i>
              <i className="ar-orbit ar-orbit-three"><Sparkles size={18} /></i>
            </div>
          </section>

          <div className="auto-report-container">
            {renderProgressIndicator()}

            {pageNotice && (
              <div className={`ar-notice ar-notice-${pageNotice.type}`} role="alert">
                {pageNotice.text}
              </div>
            )}

            {currentStep === 1 && renderStudentInput()}
            {currentStep === 2 && renderClassDetails()}
            {currentStep === 3 && renderStudentDetails()}
            {currentStep === 4 && renderReports()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AutoReport;
